(function () {
    const CART_OPEN_SELECTOR = '[data-ch-open="cart"]';

    /* Drawer motion lives in CSS custom properties; never re-hardcode it here. */
    function drawerTiming() {
        if (window.WCWYDrawerController && window.WCWYDrawerController.timing) {
            return window.WCWYDrawerController.timing();
        }
        return { open: 360, close: 260, openSettle: 380, closeFallback: 340 };
    }

    /* Start the slide on a frame the browser has already painted. */
    function afterPaint(fn) {
        if (window.WCWYDrawerController && window.WCWYDrawerController.afterPaint) {
            return window.WCWYDrawerController.afterPaint(fn);
        }
        return window.requestAnimationFrame(function () {
            window.requestAnimationFrame(fn);
        });
    }

    function isModifiedClick(event) {
        return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
    }

    function isPrimaryClick(event) {
        if (typeof event.button === 'number' && event.button !== 0) return false;
        return !isModifiedClick(event);
    }

    function markCartOpenTriggersNoInstant() {
        document.querySelectorAll(CART_OPEN_SELECTOR).forEach((trigger) => {
            trigger.setAttribute('data-no-instant', 'true');
        });
    }

    function openCartPanelFallback() {
        const panel = document.querySelector('[data-ch-panel="cart"]');
        if (!panel) return;

        panel.classList.add('is-primed');
        panel.classList.add('is-mounted');
        if (window.WCWYDrawerController) {
            window.WCWYDrawerController.setInert(panel, false);
        }
        document.querySelectorAll(CART_OPEN_SELECTOR).forEach((trigger) => {
            trigger.setAttribute('aria-expanded', 'true');
        });
        if (window.WCWYDrawerController) {
            window.WCWYDrawerController.open('cart');
        }
        panel.setAttribute('aria-hidden', 'false');
        document.body.classList.add('cart-open');
        window.requestAnimationFrame(() => {
            panel.classList.add('is-open');
        });
    }

    function bindGlobalCartOpenBridge() {
        if (window.__wcwyCartOpenBridgeBound) return;
        window.__wcwyCartOpenBridgeBound = true;

        markCartOpenTriggersNoInstant();

        document.addEventListener('click', (event) => {
            const target = event.target;
            if (!target || typeof target.closest !== 'function') return;

            const trigger = target.closest(CART_OPEN_SELECTOR);
            if (!trigger) return;
            if (!isPrimaryClick(event)) return;

            event.preventDefault();
            event.stopPropagation();

            if (window.CartDrawer && typeof window.CartDrawer.open === 'function') {
                window.CartDrawer.open();
                return;
            }

            openCartPanelFallback();
        }, true);
    }

    bindGlobalCartOpenBridge();

    const CartDrawer = {
        init: function () {
            this.selectors = {
                panel: '[data-ch-panel="cart"]',
                drawer: '[data-cart-drawer]',
                openBtns: CART_OPEN_SELECTOR,
                closeBtns: '[data-cart-close]',
                content: '[data-cart-content]',
                count: '[data-cart-count]',
                subtotal: '[data-cart-subtotal]',
                footer: '[data-cart-footer]',
                template: '#cart-item-template'
            };

            this.el = document.querySelector(this.selectors.panel);
            if (!this.el) return;

            this.drawer = this.el.querySelector(this.selectors.drawer);
            this.content = this.el.querySelector(this.selectors.content);
            this.footer = this.el.querySelector(this.selectors.footer);
            this.template = document.querySelector(this.selectors.template);
            this.moneyFormat = (document.body && document.body.dataset && document.body.dataset.moneyFormat) || '';
            this.productCache = {};
            this.activeDropdown = null;
            this.lastCart = this.readCachedCart();
            this.pendingOpenFrame = null;
            this.closeTimer = null;
            this.closeTransitionHandler = null;
            this.openSettleTimer = null;
            this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

            if (window.WCWYDrawerController) {
                window.WCWYDrawerController.register('cart', {
                    drawer: this.drawer || this.el,
                    overlay: this.el.querySelector('.ch-panel__ov'),
                    focusTarget: '[data-cart-close]',
                    isOpen: () => this.el.classList.contains('is-open'),
                    close: () => this.close()
                });
            }

            this.mirrorHeaderLogo();
            this.bindEvents();
            this.primePanel();
        },

        /**
         * Lay the panel out once, at idle after load, so the first open runs a
         * transform and nothing else — the mobile menu's model, which is the
         * only drawer in the theme that opens smoothly on the first try.
         *
         * The cached cart is rendered here too. Painting it at open time was
         * still the right call versus fetching, but it left the line items'
         * layout inside the interaction; doing it now means even a shopper's
         * first cart open has real content already laid out. render() sets
         * hasRenderedCart, which open() and fetchCart() both honour, so this
         * work is not repeated.
         */
        primePanel: function () {
            const controller = window.WCWYDrawerController;
            if (!controller || !controller.prime) {
                /* No controller: keep the old behaviour rather than leaving a
                   permanently inert panel that can never be revealed. */
                return;
            }

            controller.setInert(this.el, true);

            controller.prime(() => {
                if (!this.el) return;
                this.el.classList.add('is-primed');
                if (this.lastCart && !this.hasRenderedCart) this.render(this.lastCart);
                /* Force the layout now, inside the idle callback, rather than
                   leaving it for whichever frame happens to touch the panel
                   next — which could be the opening one. */
                if (this.drawer) this.drawer.offsetHeight;
            });
        },

        /**
         * Cache-first cart.
         *
         * The drawer used to open onto a skeleton and hold the real render until
         * the slide had finished, so the swap could not land mid-transform. That
         * traded one problem for a worse one: the footer is server-rendered and
         * on screen from frame one, so the body swap became a *visible second
         * event* a beat after the panel landed — "the buttons push first, then
         * the whole thing".
         *
         * The cart the drawer showed last time is almost always still correct, so
         * keep it in sessionStorage and paint it synchronously at open, before
         * the mount layout is forced. The panel slides in already populated, and
         * /cart.js only touches the DOM when it actually disagrees. No skeleton,
         * no second event. sessionStorage rather than localStorage: a cart is
         * per-tab-session state and must not outlive it.
         */
        cacheKey: 'wcwy:cart-drawer:last',

        readCachedCart: function () {
            try {
                const raw = window.sessionStorage.getItem(this.cacheKey);
                if (!raw) return null;
                const cart = JSON.parse(raw);
                // Anything without item_count is not a cart object we can render.
                return cart && typeof cart.item_count === 'number' ? cart : null;
            } catch (error) {
                return null;
            }
        },

        /** Single place that owns lastCart, so the cache can never drift from it. */
        setCart: function (cart) {
            this.lastCart = cart;
            try {
                window.sessionStorage.setItem(this.cacheKey, JSON.stringify(cart));
            } catch (error) {
                /* Private mode / quota — the in-memory copy still works. */
            }
        },

        /**
         * Cheap equality for "is a re-render worth it". Compares only what the
         * drawer body actually paints; ignores fields like note or attributes.
         */
        cartSignature: function (cart) {
            if (!cart) return '';
            return [cart.item_count, cart.total_price].concat(
                (cart.items || []).map((item) => [
                    item.key,
                    item.quantity,
                    item.line_price,
                    item.image || ''
                ].join('~'))
            ).join('|');
        },

        /**
         * The drawer cannot read the header section's logo setting from Liquid,
         * so mirror the header's rendered light logo into the drawer header.
         * The drawer sits on #0B0B0D, which is what the light variant is for.
         * Same image the header already loaded, so this costs no extra request.
         */
        mirrorHeaderLogo: function () {
            const slot = this.el.querySelector('[data-cart-logo-slot]');
            if (!slot) return;

            const current = slot.querySelector('[data-cart-logo]');
            // An explicit upload in the Cart Drawer section wins.
            if (current && current.hasAttribute('data-cart-logo-locked')) return;

            const header = document.querySelector('#kith-header') || document;
            const source = header.querySelector('.kith-logo--light')
                || header.querySelector('.kith-logo__img')
                || null;

            if (!source) return;

            const logo = document.createElement('img');
            logo.className = 'cart-drawer__logo';
            logo.setAttribute('data-cart-logo', '');
            logo.alt = source.alt || '';
            logo.decoding = 'async';
            if (source.currentSrc || source.src) logo.src = source.currentSrc || source.src;
            if (source.srcset) logo.srcset = source.srcset;
            if (source.sizes) logo.sizes = source.sizes;
            // Carry the intrinsic ratio so the drawer header doesn't reflow on
            // paint. naturalWidth is 0 until the header image decodes, so fall
            // back to its markup attributes.
            const w = source.naturalWidth || parseInt(source.getAttribute('width'), 10);
            const h = source.naturalHeight || parseInt(source.getAttribute('height'), 10);
            if (w && h) {
                logo.width = w;
                logo.height = h;
            }

            if (current) {
                current.replaceWith(logo);
            } else {
                slot.appendChild(logo);
            }
        },

        bindEvents: function () {
            markCartOpenTriggersNoInstant();

            this.el.querySelectorAll(this.selectors.closeBtns).forEach((button) => {
                button.addEventListener('click', (event) => {
                    event.preventDefault();
                    this.close();
                });
            });

            if (this.drawer) {
                this.drawer.addEventListener('load', (event) => {
                    if (!event.target.matches('.item-image')) return;
                    this.markImageLoaded(event.target);
                }, true);
            }

            this.el.addEventListener('click', (event) => {
                const optionTrigger = event.target.closest('[data-cart-option-trigger]');
                if (optionTrigger) {
                    event.preventDefault();
                    event.stopPropagation();

                    const row = optionTrigger.closest('.cart-item');
                    if (row && row.classList.contains('is-busy')) return;

                    const col = optionTrigger.closest('.opt-col');
                    const dropdown = col ? col.querySelector('.opt-dropdown') : null;
                    if (!col || !dropdown) return;

                    const isOpen = optionTrigger.getAttribute('aria-expanded') === 'true';
                    if (isOpen) {
                        this.closeAllDropdowns();
                    } else {
                        this.openDropdown(optionTrigger, dropdown, row);
                    }
                    return;
                }

                const optionItem = event.target.closest('[data-cart-option-item]');
                if (optionItem) {
                    event.preventDefault();
                    event.stopPropagation();

                    if (optionItem.getAttribute('aria-disabled') === 'true') return;

                    const col = optionItem.closest('.opt-col');
                    const row = optionItem.closest('.cart-item');
                    if (!col || !row || row.classList.contains('is-busy')) return;

                    const key = row.dataset.lineItemKey || '';
                    const handle = row.dataset.productHandle || '';
                    const product = this.productCache[handle];
                    const optionIndex = parseInt(col.dataset.optionIndex || '', 10);
                    const nextValue = optionItem.dataset.value || optionItem.textContent.trim();

                    const trigger = col.querySelector('.opt-trigger');
                    const triggerValue = trigger ? trigger.querySelector('.opt-value') : null;
                    if (triggerValue) {
                        triggerValue.textContent = nextValue;
                    }

                    col.querySelectorAll('[data-cart-option-item]').forEach((entry) => {
                        entry.setAttribute('aria-selected', entry === optionItem ? 'true' : 'false');
                    });

                    if (!product || !Number.isInteger(optionIndex)) {
                        this.closeAllDropdowns();
                        return;
                    }

                    const selectedOptions = this.getSelectedOptions(row, product.options.length);
                    selectedOptions[optionIndex] = nextValue;

                    const variant = product.variants.find((entry) => {
                        return entry.options.every((value, index) => value === selectedOptions[index]);
                    });

                    if (!variant) {
                        this.refreshOptionControls(row, product, selectedOptions);
                        this.closeAllDropdowns();
                        return;
                    }

                    if (String(variant.id) === String(row.dataset.variantId)) {
                        this.refreshOptionControls(row, product, selectedOptions);
                        this.closeAllDropdowns();
                        return;
                    }

                    row.dataset.variantId = String(variant.id);
                    this.changeVariant(key, variant.id, {
                        row: row,
                        trigger: trigger,
                        properties: this.getRowProperties(row),
                        sellingPlanId: row.dataset.sellingPlanId || ''
                    });
                    return;
                }

                const qtyButton = event.target.closest('[data-qty-change]');
                if (qtyButton) {
                    event.preventDefault();

                    const row = qtyButton.closest('.cart-item');
                    if (!row || row.classList.contains('is-busy')) return;

                    const key = qtyButton.dataset.key || '';
                    const qtyValue = row.querySelector('.qty-num');
                    const currentQty = qtyValue ? parseInt(qtyValue.textContent || '0', 10) : 0;
                    const delta = parseInt(qtyButton.dataset.qtyChange || '0', 10);
                    const nextQty = currentQty + delta;

                    if (nextQty < 0) return;

                    this.updateItem(key, nextQty, {
                        row: row,
                        trigger: qtyButton
                    });
                    return;
                }

                const removeButton = event.target.closest('[data-cart-remove]');
                if (removeButton) {
                    event.preventDefault();

                    const row = removeButton.closest('.cart-item');
                    if (!row || row.classList.contains('is-busy')) return;

                    this.updateItem(removeButton.dataset.cartRemove || '', 0, {
                        row: row,
                        trigger: removeButton
                    });
                }
            });

            document.addEventListener('click', (event) => {
                if (!this.el.classList.contains('is-open')) return;
                if (!this.activeDropdown) return;
                if (event.target.closest('.opt-col')) return;
                this.closeAllDropdowns();
            });

            document.addEventListener('keydown', (event) => {
                if (event.key === 'Escape') {
                    this.closeAllDropdowns();
                }
            });

            if (this.content) {
                this.content.addEventListener('scroll', () => {
                    if (!this.activeDropdown) return;
                    this.closeAllDropdowns();
                }, { passive: true });
            }

            document.addEventListener('cart:updated', (event) => {
                if (event.detail && event.detail.source === 'cart-drawer') return;
                this.fetchCart();
            });
        },

        open: function (refresh) {
            const shouldRefresh = refresh !== false;
            if (this.el.classList.contains('is-open')) {
                if (shouldRefresh) this.fetchCart();
                return;
            }

            if (this.closeTransitionHandler && this.drawer) {
                this.drawer.removeEventListener('transitionend', this.closeTransitionHandler);
                this.closeTransitionHandler = null;
            }
            window.clearTimeout(this.closeTimer);
            if (this.pendingOpenFrame) {
                window.cancelAnimationFrame(this.pendingOpenFrame);
                this.pendingOpenFrame = null;
            }

            this.el.classList.add('is-mounted');
            this.el.setAttribute('aria-hidden', 'false');
            if (window.WCWYDrawerController) {
                window.WCWYDrawerController.setInert(this.el, false);
            }
            document.querySelectorAll(this.selectors.openBtns).forEach((trigger) => {
                trigger.setAttribute('aria-expanded', 'true');
            });
            if (window.WCWYDrawerController) {
                window.WCWYDrawerController.open('cart');
            } else {
                document.documentElement.style.overflow = 'hidden';
                document.body.style.overflow = 'hidden';
            }
            document.body.classList.add('cart-open');

            // Paint the cached cart before anything animates, so the panel slides
            // in populated instead of sliding in as a skeleton and then filling.
            // Usually already done by primePanel; this covers the first open
            // after an add-to-cart that beat the idle callback.
            if (this.lastCart && !this.hasRenderedCart) this.render(this.lastCart);

            if (this.prefersReducedMotion) {
                this.el.classList.add('is-open');
                const closeButton = this.el.querySelector('[data-cart-close]');
                if (closeButton) {
                    window.setTimeout(() => closeButton.focus({ preventScroll: true }), 0);
                }
            } else {
                // Pay the mount layout cost now, synchronously, while nothing is
                // animating — then let the browser paint a clean frame before the
                // transform starts. See WCWYDrawerController.afterPaint.
                if (this.drawer) this.drawer.offsetHeight;

                this.pendingOpenFrame = afterPaint(() => {
                    this.el.classList.add('is-open');
                    const closeButton = this.el.querySelector('[data-cart-close]');
                    if (closeButton) {
                        window.setTimeout(() => closeButton.focus({ preventScroll: true }), drawerTiming().openSettle);
                    }
                    this.pendingOpenFrame = null;
                });
            }

            if (shouldRefresh) {
                this.fetchCart();
            }
        },

        close: function () {
            if (this.pendingOpenFrame) {
                window.cancelAnimationFrame(this.pendingOpenFrame);
                this.pendingOpenFrame = null;
            }

            this.el.classList.remove('is-open');
            this.el.setAttribute('aria-hidden', 'true');
            document.querySelectorAll(this.selectors.openBtns).forEach((trigger) => {
                trigger.setAttribute('aria-expanded', 'false');
            });
            document.body.classList.remove('cart-open');
            if (window.WCWYDrawerController) {
                window.WCWYDrawerController.close('cart');
            } else {
                document.documentElement.style.overflow = '';
                document.body.style.overflow = '';
            }
            this.closeAllDropdowns();

            if (this.closeTransitionHandler && this.drawer) {
                this.drawer.removeEventListener('transitionend', this.closeTransitionHandler);
                this.closeTransitionHandler = null;
            }
            window.clearTimeout(this.closeTimer);
            window.clearTimeout(this.openSettleTimer);
            this.openSettleTimer = null;

            /* Drops the compositor hint only. is-primed deliberately survives:
               re-hiding the subtree on close is what made every open after the
               first re-pay part of the mount cost. */
            const finalizeClose = () => {
                this.el.classList.remove('is-mounted');
                if (window.WCWYDrawerController) {
                    window.WCWYDrawerController.setInert(this.el, true);
                }
            };

            if (this.prefersReducedMotion || !this.drawer) {
                finalizeClose();
                return;
            }

            this.closeTransitionHandler = (event) => {
                if (event.target !== this.drawer || event.propertyName !== 'transform') return;
                this.drawer.removeEventListener('transitionend', this.closeTransitionHandler);
                this.closeTransitionHandler = null;
                window.clearTimeout(this.closeTimer);
                finalizeClose();
            };
            this.drawer.addEventListener('transitionend', this.closeTransitionHandler);
            this.closeTimer = window.setTimeout(() => {
                if (this.closeTransitionHandler && this.drawer) {
                    this.drawer.removeEventListener('transitionend', this.closeTransitionHandler);
                    this.closeTransitionHandler = null;
                }
                finalizeClose();
            }, drawerTiming().closeFallback);
        },

        fetchCart: function () {
            return fetch('/cart.js')
                .then((response) => response.json())
                .then((cart) => {
                    // Reconcile against what is already on screen. When the cached
                    // render was right — the overwhelmingly common case — this is
                    // the whole cost of the refresh: no relayout of the layer that
                    // is mid-transform, and nothing visibly changes under the user.
                    const unchanged = this.cartSignature(cart) === this.cartSignature(this.lastCart);
                    this.setCart(cart);
                    if (!unchanged || !this.hasRenderedCart) this.render(cart);
                    return cart;
                })
                .catch((error) => {
                    console.error('Cart fetch error:', error);
                    return null;
                });
        },

        addItems: function (items, options) {
            const settings = options || {};
            return fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: items })
            })
                .then(async (response) => {
                    const data = await response.json();
                    if (!response.ok) {
                        throw new Error(data.description || data.message || 'Unable to add this item to cart.');
                    }
                    return data;
                })
                .then((data) => {
                    if (settings.open !== false) {
                        this.open(false);
                    }
                    return this.fetchCart().then((cart) => {
                        document.dispatchEvent(new CustomEvent('cart:updated', {
                            detail: {
                                source: 'cart-drawer',
                                cart: cart
                            }
                        }));
                        return data;
                    });
                })
                .catch((error) => {
                    console.error('Cart add error:', error);
                    throw error;
                });
        },

        render: function (cart) {
            document.querySelectorAll(this.selectors.count).forEach((node) => {
                node.textContent = cart.item_count;
                if (node.classList.contains('kith-header__cart-count')) {
                    node.hidden = cart.item_count === 0;
                }
            });

            this.hasRenderedCart = true;

            const subtotal = this.el.querySelector(this.selectors.subtotal);
            if (subtotal) {
                subtotal.textContent = this.formatMoney(cart.total_price);
            }

            if (!this.content) return;

            if (!cart.item_count) {
                this.content.innerHTML = [
                    '<div class="cart-drawer__empty">',
                    '  <p class="cart-drawer__empty-text">Your cart is empty.</p>',
                    '  <a href="/collections/all" class="cart-drawer__empty-link">Shop All</a>',
                    '</div>'
                ].join('');

                if (this.footer) {
                    this.footer.hidden = true;
                }
                return;
            }

            if (this.footer) {
                this.footer.hidden = false;
            }

            if (!this.template) return;

            const template = this.template.innerHTML;
            let html = '';

            cart.items.forEach((item) => {
                const safeTitle = this.escapeHtml(item.product_title || '');
                const imageHtml = item.image
                    ? `<img class="item-image" src="${this.escapeHtml(item.image)}" alt="${safeTitle}" width="88" height="112" loading="lazy">`
                    : '<span class="item-image item-image--empty" aria-hidden="true"></span>';
                const optionsHtml = this.buildOptionsHtml(item);
                const propertiesHtml = this.buildPropertiesHtml(item);
                const tooltipHtml = this.buildOneSizeTooltip(item);
                const propertiesJson = this.escapeHtml(this.encodeRowProperties(item.properties || {}));
                const sellingPlanId = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
                    ? String(item.selling_plan_allocation.selling_plan.id)
                    : '';

                let itemHtml = template;
                itemHtml = itemHtml.replace(/\$\{key\}/g, item.key);
                itemHtml = itemHtml.replace(/\$\{variant_id\}/g, item.id);
                itemHtml = itemHtml.replace(/\$\{handle\}/g, item.handle || '');
                itemHtml = itemHtml.replace(/\$\{url\}/g, item.url || '#');
                itemHtml = itemHtml.replace(/__IMAGE_HTML__/g, imageHtml);
                itemHtml = itemHtml.replace(/\$\{product_title\}/g, safeTitle);
                itemHtml = itemHtml.replace(/\$\{properties_json\}/g, propertiesJson);
                itemHtml = itemHtml.replace(/\$\{selling_plan_id\}/g, this.escapeHtml(sellingPlanId));
                itemHtml = itemHtml.replace(/__ONE_SIZE_TOOLTIP__/g, tooltipHtml);
                itemHtml = itemHtml.replace(/__OPTIONS_HTML__/g, optionsHtml);
                itemHtml = itemHtml.replace(/__PROPERTIES_HTML__/g, propertiesHtml);
                itemHtml = itemHtml.replace(/\$\{quantity\}/g, item.quantity);
                itemHtml = itemHtml.replace(/\$\{price_formatted\}/g, this.formatMoney(item.line_price));

                html += itemHtml;
            });

            this.content.innerHTML = html;
            this.closeAllDropdowns();
            this.syncImageLoadState();
            this.hydrateOptionControls(cart.items);
        },

        buildOneSizeTooltip: function (item) {
            const optionEntries = this.getRenderableOptions(item);
            if (optionEntries.length) {
                return '';
            }
            return '<div class="item-one-size-badge">One Size Only</div>';
        },

        buildOptionsHtml: function (item) {
            const optionEntries = this.getRenderableOptions(item);
            if (!optionEntries.length) {
                return '<div class="item-options" data-option-count="0"></div>';
            }

            let html = `<div class="item-options" data-option-count="${optionEntries.length}">`;
            optionEntries.forEach((entry) => {
                html += this.buildOptionHtml(entry.option, entry.index, item.key, item.handle || '');
            });
            html += '</div>';

            return html;
        },

        buildOptionHtml: function (option, optionIndex, itemKey, handle) {
            const optionName = this.escapeHtml(String(option.name || '').toUpperCase());
            const optionValue = this.escapeHtml(option.value || '');

            return [
                `<div class="opt-col" data-option-index="${optionIndex}" data-item-key="${itemKey}" data-product-handle="${handle}">`,
                `  <div class="opt-label">${optionName}</div>`,
                `  <button type="button" class="opt-trigger" data-cart-option-trigger aria-expanded="false" aria-haspopup="listbox" title="${optionValue}">`,
                `    <span class="opt-value" title="${optionValue}">${optionValue}</span>`,
                '    <span class="opt-caret" aria-hidden="true">&#9662;</span>',
                '  </button>',
                '  <div class="opt-dropdown" role="listbox" hidden>',
                `    <button type="button" class="opt-option" data-cart-option-item data-value="${optionValue}" role="option" aria-selected="true" title="${optionValue}">`,
                `      <span title="${optionValue}">${optionValue}</span>`,
                '    </button>',
                '  </div>',
                '</div>'
            ].join('');
        },

        buildPropertiesHtml: function (item) {
            const rows = [];
            const properties = item.properties || {};

            Object.keys(properties).forEach((key) => {
                const value = properties[key];
                if (key.charAt(0) === '_') return;
                if (value === null || typeof value === 'undefined') return;
                if (String(value).trim() === '') return;

                rows.push({
                    label: key,
                    value: String(value)
                });
            });

            if (item.selling_plan_allocation && item.selling_plan_allocation.name) {
                rows.push({
                    label: 'Plan',
                    value: item.selling_plan_allocation.name
                });
            }

            if (!rows.length) return '';

            return [
                '<div class="item-properties">',
                rows.map((row) => {
                    return [
                        '<div class="item-property">',
                        `  <span class="item-property__label">${this.escapeHtml(String(row.label).toUpperCase())}</span>`,
                        `  <span class="item-property__value">${this.escapeHtml(row.value)}</span>`,
                        '</div>'
                    ].join('');
                }).join(''),
                '</div>'
            ].join('');
        },

        getRenderableOptions: function (item) {
            let options = [];

            if (Array.isArray(item.options_with_values)) {
                options = item.options_with_values;
            } else if (Array.isArray(item.options)) {
                options = item.options.map((value, index) => {
                    return {
                        name: `Option ${index + 1}`,
                        value: value
                    };
                });
            }

            return options
                .map((option, index) => ({ option: option, index: index }))
                .filter((entry) => {
                    const name = String(entry.option.name || '').trim().toLowerCase();
                    const value = String(entry.option.value || '').trim().toLowerCase();
                    return !(name === 'title' && value === 'default title');
                });
        },

        hydrateOptionControls: function (items) {
            const handles = items
                .map((item) => item.handle)
                .filter(Boolean)
                .filter((handle, index, list) => list.indexOf(handle) === index);

            const requests = handles
                .filter((handle) => !this.productCache[handle])
                .map((handle) => {
                    return fetch(`/products/${handle}.js`)
                        .then((response) => response.json())
                        .then((product) => {
                            this.productCache[handle] = product;
                        })
                        .catch(() => null);
                });

            Promise.all(requests).then(() => {
                items.forEach((item) => {
                    const product = this.productCache[item.handle];
                    const row = this.el.querySelector(`[data-line-item-key="${item.key}"]`);
                    if (!product || !row) return;

                    const selectedOptions = this.getSelectedOptions(row, product.options.length);
                    this.refreshOptionControls(row, product, selectedOptions);
                });
            });
        },

        refreshOptionControls: function (row, product, selectedOptions) {
            // Check if this is a default variant (first variant in product)
            const isDefaultVariant = product.variants.length > 0 && String(product.variants[0].id) === String(row.dataset.variantId);
            const hasMultipleVariants = product.variants.length > 1;

            row.querySelectorAll('.opt-col').forEach((col, colIndex) => {
                const optionIndex = parseInt(col.dataset.optionIndex || '', 10);
                if (!Number.isInteger(optionIndex)) return;

                const currentValue = selectedOptions[optionIndex] || '';
                const valueNode = col.querySelector('.opt-value');
                const dropdown = col.querySelector('.opt-dropdown');
                const trigger = col.querySelector('.opt-trigger');

                if (valueNode) {
                    valueNode.textContent = currentValue;
                }

                // Add variant hint (pulsing animation) if on default variant and this is first option col
                if (isDefaultVariant && hasMultipleVariants && colIndex === 0) {
                    col.classList.add('variant-hint');
                } else {
                    col.classList.remove('variant-hint');
                }

                if (!dropdown) return;

                const uniqueValues = product.variants
                    .map((variant) => variant.options[optionIndex])
                    .filter(Boolean)
                    .filter((value, index, list) => list.indexOf(value) === index);

                dropdown.innerHTML = uniqueValues.map((value) => {
                    const safeValue = this.escapeHtml(value);
                    const isSelected = value === currentValue;
                    const isAvailable = this.isOptionValueAvailable(product, optionIndex, value, selectedOptions);

                    return [
                        `<button type="button" class="opt-option" data-cart-option-item data-value="${safeValue}" role="option" aria-selected="${isSelected ? 'true' : 'false'}"${isAvailable ? '' : ' aria-disabled="true"'}>`,
                        `  <span>${safeValue}</span>`,
                        isAvailable ? '' : '  <span class="opt-option-note">Sold out</span>',
                        '</button>'
                    ].join('');
                }).join('');
            });
        },

        isOptionValueAvailable: function (product, optionIndex, nextValue, selectedOptions) {
            return product.variants.some((variant) => {
                if (!variant.available) return false;

                return variant.options.every((value, index) => {
                    if (index === optionIndex) {
                        return value === nextValue;
                    }

                    return !selectedOptions[index] || value === selectedOptions[index];
                });
            });
        },

        getSelectedOptions: function (row, optionCount) {
            const selected = new Array(optionCount).fill('');

            row.querySelectorAll('.opt-col').forEach((col) => {
                const optionIndex = parseInt(col.dataset.optionIndex || '', 10);
                const valueNode = col.querySelector('.opt-value');
                if (!Number.isInteger(optionIndex) || !valueNode) return;
                selected[optionIndex] = valueNode.textContent.trim();
            });

            return selected;
        },

        openDropdown: function (trigger, dropdown, row) {
            this.closeAllDropdowns();
            dropdown.hidden = false;
            trigger.setAttribute('aria-expanded', 'true');
            if (row) {
                row.classList.add('is-option-open');
            }
            this.activeDropdown = {
                trigger: trigger,
                dropdown: dropdown,
                row: row
            };
        },

        closeAllDropdowns: function () {
            if (this.activeDropdown) {
                this.activeDropdown.dropdown.hidden = true;
                this.activeDropdown.trigger.setAttribute('aria-expanded', 'false');
                if (this.activeDropdown.row) {
                    this.activeDropdown.row.classList.remove('is-option-open');
                }
                this.activeDropdown = null;
            }

            this.el.querySelectorAll('.opt-trigger[aria-expanded="true"]').forEach((trigger) => {
                trigger.setAttribute('aria-expanded', 'false');
            });

            this.el.querySelectorAll('.opt-dropdown').forEach((dropdown) => {
                dropdown.hidden = true;
            });

            this.el.querySelectorAll('.cart-item.is-option-open').forEach((row) => {
                row.classList.remove('is-option-open');
            });
        },

        markImageLoaded: function (image) {
            if (!image) return;

            const skeleton = image.closest('[data-skeleton]');
            if (!skeleton) return;

            skeleton.classList.add('loaded');

            const shimmer = skeleton.querySelector('.wcwy-image-shimmer');
            if (shimmer) {
                shimmer.classList.add('shimmer-loaded');
            }
        },

        syncImageLoadState: function () {
            if (!this.content) return;

            this.content.querySelectorAll('[data-skeleton]').forEach((skeleton) => {
                const image = skeleton.querySelector('.item-image');
                if (!image || image.classList.contains('item-image--empty')) {
                    skeleton.classList.add('loaded');
                    const shimmer = skeleton.querySelector('.wcwy-image-shimmer');
                    if (shimmer) {
                        shimmer.classList.add('shimmer-loaded');
                    }
                    return;
                }

                if (image.complete && image.naturalWidth > 0) {
                    this.markImageLoaded(image);
                }
            });
        },

        updateItem: function (key, quantity, options) {
            const row = (options && options.row) || this.el.querySelector(`[data-line-item-key="${key}"]`);
            const errorNode = this.el.querySelector(`[data-line-item-error="${key}"]`);

            if (errorNode) {
                errorNode.textContent = '';
            }

            this.closeAllDropdowns();
            this.setRowBusy(row, options && options.trigger);

            fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: key,
                    quantity: quantity
                })
            })
                .then(async (response) => {
                    const data = await response.json();
                    if (!response.ok) {
                        throw {
                            status: response.status,
                            description: data.description || data.message || 'Unable to update this item.'
                        };
                    }
                    return data;
                })
                .then((cart) => {
                    this.setCart(cart);

                    if (quantity === 0 && row) {
                        row.classList.add('is-removing');
                        window.setTimeout(() => {
                            this.render(cart);
                            document.dispatchEvent(new CustomEvent('cart:updated', {
                                detail: {
                                    source: 'cart-drawer',
                                    cart: cart
                                }
                            }));
                        }, 260);
                        return;
                    }

                    this.render(cart);
                    document.dispatchEvent(new CustomEvent('cart:updated', {
                        detail: {
                            source: 'cart-drawer',
                            cart: cart
                        }
                    }));
                })
                .catch((error) => {
                    this.clearRowBusy(row);
                    if (errorNode) {
                        let message = error.description || 'We encountered an issue. Please try again.';
                        if (message.includes('due to availability')) {
                            message = message.replace('due to availability', 'because stock is limited');
                        }
                        errorNode.textContent = message;
                    } else {
                        console.error('Cart update error:', error);
                    }
                });
        },

        changeVariant: async function (key, variantId, options) {
            const row = (options && options.row) || this.el.querySelector(`[data-line-item-key="${key}"]`);
            if (!row) return;

            const errorNode = row.querySelector('.item-error');
            if (errorNode) {
                errorNode.textContent = '';
            }

            const quantityNode = row.querySelector('.qty-num');
            const quantity = quantityNode ? parseInt(quantityNode.textContent || '1', 10) : 1;
            const properties = (options && options.properties) || this.getRowProperties(row);
            const sellingPlanId = (options && options.sellingPlanId) || row.dataset.sellingPlanId || '';

            this.closeAllDropdowns();
            this.setRowBusy(row, options && options.trigger);

            const addPayload = {
                id: Number(variantId),
                quantity: quantity
            };

            if (properties && Object.keys(properties).length) {
                addPayload.properties = properties;
            }

            if (sellingPlanId) {
                addPayload.selling_plan = Number(sellingPlanId);
            }

            try {
                // Step 1: add new variant
                let response = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        items: [addPayload]
                    })
                });

                let data = await response.json();
                if (!response.ok) {
                    throw {
                        status: response.status,
                        description: data.description || data.message || 'Unable to change this option.'
                    };
                }

                // Step 2: remove old line item
                response = await fetch('/cart/change.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        id: key,
                        quantity: 0
                    })
                });

                data = await response.json();
                if (!response.ok) {
                    throw {
                        status: response.status,
                        description: data.description || data.message || 'Unable to finish changing this option.'
                    };
                }

                // Step 3: fetch updated cart and render
                const cart = data;
                this.setCart(cart);
                this.render(cart);
                document.dispatchEvent(new CustomEvent('cart:updated', {
                    detail: {
                        source: 'cart-drawer',
                        cart: cart
                    }
                }));
            } catch (error) {
                console.error('[CartDrawer] changeVariant failed:', error);
                this.clearRowBusy(row);
                if (errorNode) {
                    let message = error.description || 'We encountered an issue changing this option. Please try again.';
                    if (message.includes('due to availability')) {
                        message = message.replace('due to availability', 'because stock is limited');
                    }
                    errorNode.textContent = message;
                }
                this.fetchCart();
            }
        },

        setRowBusy: function (row, trigger) {
            if (!row) return;

            row.classList.add('is-busy');
            row.querySelectorAll('.qty-btn, .opt-trigger, .item-remove').forEach((control) => {
                if ('disabled' in control) {
                    control.disabled = true;
                }
                control.setAttribute('aria-busy', 'true');
                control.classList.remove('is-loading');
            });

            if (trigger) {
                trigger.classList.add('is-loading');
            }
        },

        clearRowBusy: function (row) {
            if (!row) return;

            row.classList.remove('is-busy');
            row.querySelectorAll('.qty-btn, .opt-trigger, .item-remove').forEach((control) => {
                if ('disabled' in control) {
                    control.disabled = false;
                }
                control.removeAttribute('aria-busy');
                control.classList.remove('is-loading');
            });
        },

        getRowProperties: function (row) {
            if (!row || !row.dataset.properties) return {};

            try {
                return JSON.parse(decodeURIComponent(row.dataset.properties));
            } catch (error) {
                return {};
            }
        },

        encodeRowProperties: function (properties) {
            const cleaned = {};

            Object.keys(properties || {}).forEach((key) => {
                const value = properties[key];
                if (value === null || typeof value === 'undefined') return;
                cleaned[key] = value;
            });

            return encodeURIComponent(JSON.stringify(cleaned));
        },

        escapeHtml: function (value) {
            return String(value)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');
        },

        formatMoney: function (cents) {
            if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && this.moneyFormat) {
                return window.Shopify.formatMoney(cents, this.moneyFormat);
            }

            return '$' + Number(cents / 100).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        }
    };

    function initCartDrawer() {
        CartDrawer.init();
        window.CartDrawer = CartDrawer;
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initCartDrawer, { once: true });
    } else {
        initCartDrawer();
    }
})();
